import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import PageBody from 'Common/Custom/PageBody';
import { HeadingOne } from 'Common/Custom/HeadingOne';
import { GetDashboardData } from 'adminApi/HrDashboardApi/HrDashboardApi';
import { attendanceTrendsOptions } from './Components/AttendanceTrendsChart';
import Spinner from 'Common/Globle/Spinner';
import defaultAvatar from '../../assets/images/avatar.png';

const IndexHr = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await GetDashboardData();
                if (response.success) {
                    setDashboardData(response.data);
                }
            } catch (error) {
                console.error("Dashboard Fetch Error:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    if (loading) return <PageBody card={false}><div className="col-12 text-center py-5"><Spinner isSpinner /></div></PageBody>;

    const { stats, awards, interviews, trends, performance, topPerformers, recentLeaves } = dashboardData || {};

    // Performance Chart Config: Flow from 1 to 5 stars
    const fullPerformanceData = [1, 2, 3, 4, 5].map(rating => {
        const found = (performance || []).find(p => p.rating === rating);
        return { rating, count: found ? found.count : 0 };
    });

    const performanceChartOptions = {
        chart: {
            type: 'bar',
            height: 280,
            toolbar: { show: false },
            zoom: { enabled: false },
            dropShadow: {
                enabled: true,
                top: 2,
                left: 2,
                blur: 4,
                opacity: 0.1
            }
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '55%',
                borderRadius: 10,
                borderRadiusApplication: 'end',
                distributed: true,
                dataLabels: { position: 'top' }
            }
        },
        colors: [
            '#f43f5e', // 1 Star: Rose Red
            '#f59e0b', // 2 Stars: Amber
            '#3b82f6', // 3 Stars: Blue
            '#06b6d4', // 4 Stars: Cyan
            '#10b981'  // 5 Stars: Emerald
        ],
        fill: {
            type: 'gradient',
            gradient: {
                shade: 'light',
                type: "vertical",
                shadeIntensity: 0.2,
                gradientToColors: undefined,
                inverseColors: true,
                opacityFrom: 0.85,
                opacityTo: 0.95,
                stops: [0, 100]
            }
        },
        dataLabels: {
            enabled: false,
        },
        xaxis: {
            categories: ['1 Star', '2 Stars', '3 Stars', '4 Stars', '5 Stars'],
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: {
                style: {
                    colors: '#94a3b8',
                    fontSize: '12px',
                    fontWeight: 500
                }
            }
        },
        yaxis: {
            show: true,
            labels: {
                style: {
                    colors: '#94a3b8',
                    fontSize: '11px'
                }
            }
        },
        tooltip: {
            theme: 'light',
            y: {
                formatter: (val) => `${val} Employees`
            }
        },
        grid: {
            borderColor: '#f1f5f9',
            strokeDashArray: 4,
            yaxis: { lines: { show: true } },
            xaxis: { lines: { show: false } }
        },
        legend: { show: false }
    };

    const getStatusBadge = (status) => {
        const badges = {
            'approved': 'bg-success',
            'pending': 'bg-warning',
            'rejected': 'bg-danger'
        };
        return <span className={`badge ${badges[status?.toLowerCase()] || 'bg-secondary'} x-small`}>{status}</span>;
    };

    return (
        <PageBody
            card={false}
            headerchildren={
                <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                    <div>
                        <HeadingOne className="mb-0">HR Dashboard</HeadingOne>
                        <p className="text-muted small mb-0">Welcome back! Here's what's happening today.</p>
                    </div>
                    <div className="text-muted small fw-semibold">
                        {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                </div>
            }
        >
            {/* Top Stats Row */}
            <div className="col-12">
                <div className="row g-3">
                    {[
                        {
                            label: 'Total Employees',
                            value: stats?.total_employees || 0,
                            color: 'primary',
                            svg: <svg xmlns="http://www.w3.org/2000/svg" className="svg-stroke" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" /><path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" /><path d="M16 11l2 2l4 -4" /></svg>,
                            trend: '+5%'
                        },
                        {
                            label: 'Present Today',
                            value: stats?.present_today || 0,
                            color: 'success',
                            svg: <svg xmlns="http://www.w3.org/2000/svg" className="svg-stroke" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" /><path d="M6 21v-2a4 4 0 0 1 4 -4h4" /><path d="M15 19l2 2l4 -4" /></svg>,
                            trend: '+12%'
                        },
                        {
                            label: 'Pending Leaves',
                            value: stats?.pending_leaves || 0,
                            color: 'warning',
                            svg: <svg xmlns="http://www.w3.org/2000/svg" className="svg-stroke" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" /><path d="M12 12l3 2" /><path d="M12 7v5" /></svg>,
                            trend: '+3%'
                        },
                        {
                            label: 'Upcoming Interviews',
                            value: stats?.upcoming_interviews || 0,
                            color: 'info',
                            svg: <svg xmlns="http://www.w3.org/2000/svg" className="svg-stroke" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12z" /><path d="M16 3v4" /><path d="M8 3v4" /><path d="M4 11h16" /><path d="M11 15h1" /><path d="M12 15v3" /></svg>,
                            trend: '+8%'
                        },
                    ].map((item, id) => (
                        <div className="col-lg-3 col-md-6" key={id}>
                            <div className="card shadow-sm border-0 h-100 py-2" style={{ borderRadius: '16px' }}>
                                <div className="card-body position-relative p-3">
                                    <div className="position-absolute top-0 end-0 mt-3 me-3">
                                        <span className={`badge bg-${item.color}-subtle text-${item.color} rounded-pill border-0 px-2 py-1`} style={{ fontSize: '10px' }}>
                                            <i className="bi bi-arrow-up-right me-1"></i>{item.trend}
                                        </span>
                                    </div>
                                    <div className={`avatar lg rounded-3 bg-${item.color}-subtle text-${item.color} d-flex align-items-center justify-content-center mb-3`} style={{ width: '48px', height: '48px' }}>
                                        {item.svg}
                                    </div>
                                    <div className="text-muted small fw-semibold mb-1">{item.label}</div>
                                    <div className="h3 mb-0 fw-bold text-dark">{item.value}</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Middle Row: Recognition, Interviews & Performance */}
            <div className="col-xxl-4 col-xl-4 col-lg-6">
                <div className="card h-100 shadow-sm border-0" style={{ borderRadius: '16px' }}>
                    <div className="card-header bg-transparent border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                        <div>
                            <h6 className="mb-0 fw-bold text-dark">Staff Recognition</h6>
                            <p className="text-muted xx-small-text mb-0">Latest achievements</p>
                        </div>
                        <span className="badge bg-success-subtle text-success rounded-pill px-2">Top Talent</span>
                    </div>
                    <div className="card-body px-4 py-2">
                        {awards?.length > 0 ? awards.map((award, index) => (
                            <div className={`d-flex align-items-center py-3 ${index !== awards.length - 1 ? 'border-bottom border-light' : ''}`} key={index}>
                                <div className="avatar rounded-3 bg-primary-subtle text-primary d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="svg-stroke" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M8 21l8 0" /><path d="M12 17l0 4" /><path d="M7 4l10 0" /><path d="M17 4v8a5 5 0 0 1 -10 0v-8" /><path d="M5 9m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" /><path d="M19 9m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" /></svg>
                                </div>
                                <div className="flex-grow-1">
                                    <h6 className="fw-bold mb-0 small text-dark">{award.employee_name}</h6>
                                    <span className="text-muted x-small">{award.award_name}</span>
                                </div>
                                <div className="ms-auto">
                                    <span className="badge bg-light text-muted x-small border-0">Healthy</span>
                                </div>
                            </div>
                        )) : <div className="text-center py-4">No recent awards</div>}
                    </div>
                </div>
            </div>

            <div className="col-xxl-4 col-xl-4 col-lg-6">
                <div className="card h-100 shadow-sm border-0" style={{ borderRadius: '16px' }}>
                    <div className="card-header bg-transparent border-0 pt-4 px-4">
                        <h6 className="mb-0 fw-bold text-dark">Interview Pipeline</h6>
                        <p className="text-muted xx-small-text mb-0">Daily status breakdown</p>
                    </div>
                    <div className="card-body px-4 py-2">
                        {interviews?.length > 0 ? interviews.map((interview, index) => (
                            <div className={`d-flex align-items-center py-3 ${index !== interviews.length - 1 ? 'border-bottom border-light' : ''}`} key={index}>
                                <div className="avatar rounded-3 bg-info-subtle text-info d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="svg-stroke" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M4 18v3s1 -.5 2 -.5s2 .5 2 .5v-3h-4z" /><path d="M12 21a1.996 1.996 0 0 1 -1.414 -.586a2 2 0 0 1 -.586 -1.414v-10c0 -2.21 1.791 -4 4 -4s4 1.79 4 4v10a2 2 0 0 1 -2 2h-4z" /><path d="M13 14h4" /><path d="M13 10h4" /></svg>
                                </div>
                                <div className="flex-grow-1">
                                    <h6 className="fw-bold mb-0 small text-dark">{interview.candidate_name}</h6>
                                    <span className="text-muted x-small">{interview.position_name}</span>
                                </div>
                                <div className="ms-auto text-end">
                                    <span className="badge bg-warning-subtle text-warning rounded-pill x-small px-2">Busy</span>
                                </div>
                            </div>
                        )) : <div className="text-center py-4">Clear schedule</div>}

                        <div className="mt-4 pt-3 border-top">
                            <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted x-small">Hiring Goal</span>
                                <span className="fw-bold text-dark x-small">70%</span>
                            </div>
                            <div className="progress" style={{ height: '8px', borderRadius: '4px' }}>
                                <div className="progress-bar bg-primary" role="progressbar" style={{ width: '70%' }}></div>
                            </div>
                            <div className="d-flex justify-content-between mt-2">
                                <span className="text-muted" style={{ fontSize: '9px' }}>Current</span>
                                <span className="text-muted" style={{ fontSize: '9px' }}>Target: 85%</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-xxl-4 col-xl-4 col-12">
                <div className="card h-100 shadow-sm border-0" style={{ borderRadius: '16px' }}>
                    <div className="card-header bg-transparent border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                        <h6 className="mb-0 fw-bold text-dark">Leave Requests</h6>
                    </div>
                    <div className="card-body px-4 py-2">
                        {recentLeaves?.length > 0 ? recentLeaves.map((leave, index) => (
                            <div className={`d-flex align-items-center py-3 ${index !== recentLeaves.length - 1 ? 'border-bottom border-light' : ''}`} key={index}>
                                <div className="avatar rounded-3 bg-danger-subtle text-danger d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="svg-stroke" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" /><path d="M12 7v5l3 3" /></svg>
                                </div>
                                <div className="flex-grow-1">
                                    <h6 className="fw-bold mb-0 small text-dark">{leave.employee_name}</h6>
                                    <span className="text-muted x-small">{leave.leave_type} • {leave.applied_days}d</span>
                                </div>
                                <div className="ms-auto">
                                    {getStatusBadge(leave.status)}
                                </div>
                            </div>
                        )) : <div className="text-center py-4">No pending leaves</div>}
                    </div>
                </div>
            </div>

            {/* Performance Row */}
            <div className="col-xxl-8 col-xl-7 col-lg-6">
                <div className="card shadow-sm border-0 h-100" style={{ borderRadius: '16px' }}>
                    <div className="card-header bg-transparent border-0 pt-4 px-4">
                        <h6 className="mb-0 fw-bold text-dark">Performance Distribution</h6>
                        <p className="text-muted small mb-0">Employee rating breakdown</p>
                    </div>
                    <div className="card-body">
                        {fullPerformanceData?.length > 0 && (
                            <Chart
                                options={performanceChartOptions}
                                series={[{ name: 'Employees', data: fullPerformanceData.map(p => p.count) }]}
                                type="bar"
                                height={260}
                            />
                        )}
                    </div>
                </div>
            </div>

            <div className="col-xxl-4 col-xl-5 col-lg-6">
                <div className="card shadow-sm border-0 h-100" style={{ borderRadius: '16px' }}>
                    <div className="card-header bg-transparent border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                        <h6 className="mb-0 fw-bold text-dark">Top Performers</h6>
                    </div>
                    <div className="card-body px-4 py-2">
                        {topPerformers?.length > 0 ? topPerformers.map((tp, index) => (
                            <div className={`d-flex align-items-center py-3 ${index !== topPerformers.length - 1 ? 'border-bottom border-light' : ''}`} key={index}>
                                <div className="avatar sm rounded-circle border border-2 border-white shadow-sm overflow-hidden me-3" style={{ width: '36px', height: '36px' }}>
                                    <img className="w-100 h-100 object-fit-cover" src={tp.profile_img || defaultAvatar} alt="profile" />
                                </div>
                                <div className="flex-grow-1">
                                    <h6 className="mb-0 small fw-bold text-dark">{tp.name}</h6>
                                    <div className="text-warning d-flex gap-1" style={{ fontSize: '9px' }}>
                                        {[...Array(5)].map((_, i) => (
                                            <i key={i} className={`bi bi-star${i < Math.round(tp.rating) ? '-fill' : ''}`}></i>
                                        ))}
                                    </div>
                                </div>
                                <div className="ms-auto">
                                    <div className="h6 mb-0 fw-bold text-primary">{Number(tp.rating).toFixed(1)}</div>
                                </div>
                            </div>
                        )) : <div className="text-center py-4 text-muted small">No performance data</div>}
                    </div>
                </div>
            </div>
            <div className="col-12">
                <div className="card shadow-sm border-0" style={{ borderRadius: '16px' }}>
                    <div className="card-header bg-transparent border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="mb-1 fw-bold text-dark">Attendance & Leave Trends</h5>
                            <p className="text-muted small mb-0">Overview of staff availability across the month</p>
                        </div>
                        <div className="d-flex gap-4">
                            <span className="small d-flex align-items-center fw-semibold text-muted">
                                <span className="d-inline-block rounded-circle me-2" style={{ width: '8px', height: '8px', background: '#3b82f6' }}></span> Attendance
                            </span>
                            <span className="small d-flex align-items-center fw-semibold text-muted">
                                <span className="d-inline-block rounded-circle me-2" style={{ width: '8px', height: '8px', background: '#f97316' }}></span> Leaves
                            </span>
                        </div>
                    </div>
                    <div className="card-body px-4 pb-4">
                        {trends?.length > 0 && (
                            <Chart
                                options={{
                                    chart: {
                                        type: 'line',
                                        height: 320,
                                        toolbar: { show: false },
                                        zoom: { enabled: false },
                                        fontFamily: 'inherit'
                                    },
                                    stroke: {
                                        width: [0, 2.5],
                                        curve: 'smooth'
                                    },
                                    plotOptions: {
                                        bar: {
                                            columnWidth: '22%',
                                            borderRadius: 4
                                        }
                                    },
                                    colors: ['#3b82f6', '#f97316'],
                                    xaxis: {
                                        categories: trends.map(t => t.month || t.date || 'Jan'),
                                        axisBorder: { show: false },
                                        axisTicks: { show: false },
                                        labels: {
                                            style: { colors: '#94a3b8', fontSize: '11px', fontWeight: 500 }
                                        }
                                    },
                                    yaxis: [
                                        {
                                            show: true,
                                            labels: { style: { colors: '#3b82f6', fontWeight: 500 } }
                                        },
                                        {
                                            opposite: true,
                                            show: true,
                                            labels: { style: { colors: '#f97316', fontWeight: 500 } }
                                        }
                                    ],
                                    grid: {
                                        borderColor: '#f1f5f9',
                                        strokeDashArray: 4,
                                        xaxis: { lines: { show: false } },
                                        yaxis: { lines: { show: true } }
                                    },
                                    legend: { show: false },
                                    tooltip: {
                                        theme: 'light',
                                        shared: true,
                                        intersect: false,
                                        y: {
                                            formatter: (val) => `${val} Employees`
                                        }
                                    },
                                    markers: {
                                        size: [0, 4],
                                        strokeWidth: 2,
                                        strokeColors: '#fff',
                                        hover: { size: 6 }
                                    }
                                }}
                                series={[
                                    { name: 'Attendance', type: 'column', data: trends.map(t => t.attendance) },
                                    { name: 'Leaves', type: 'line', data: trends.map(t => t.leaves) }
                                ]}
                                type="line"
                                height={320}
                            />
                        )}
                    </div>
                </div>
            </div>
        </PageBody>
    );
};

export default IndexHr;